// Data Contracts
import { CaseParticipant, Representation } from '@data-contracts';

// Shared
import { NO_ANSWER_ON_FILE } from '@shared/components/parties-table/parties-table.utils';

// Local
import {
  CaseParticipantWithPartyStatus,
  PARTY_STATUS,
} from '@feature/subf/services/map-filing-draft-case-participants';

/**
 * A {@link CaseParticipant} that may carry the party status resolved by
 * `mapFilingDraftCaseParticipantsToCaseParticipants`. Optional so plain
 * {@link CaseParticipant} lists remain assignable.
 */
type CaseParticipantWithOptionalStatus = CaseParticipant &
  Partial<Pick<CaseParticipantWithPartyStatus, 'partyStatus'>>;

/**
 * Flattened row interface — one row per CaseParticipant × Attorney pair.
 */
export interface CaseParticipantListRow {
  /** Unique key for identity tracking (participantId + attorneyId). */
  id: string;

  /** The party's partyId — convenience shortcut to `caseParticipant.party?.partyId`. */
  partyId: number | null;

  /** Reference back to the source CaseParticipant. */
  caseParticipant: CaseParticipant;

  /** Reference to the specific Representation for this row. */
  representation: Representation | null;

  /** Display: party name from `party.lexicalOrderName`. */
  partyName: string;

  /** Display: party type label. */
  partyTypeLabel: string;

  /** Display: "Dismissed" / "Withdrawn" for non-active participants; null otherwise. */
  partyStatusLabel: string | null;

  /** Display: attorney name from `Representation.attorneyName`. */
  attorneyName: string;

  /** Display: attorney type label. */
  attorneyTypeLabel: string;

  /** Display: firm from `Representation.attorneyOrganizationName`. */
  firmName: string;

  /** Display: service method — mapped from `Representation.serviceMethodId` when known, else trimmed `Representation.serviceMethod`. */
  serviceMethod: string;

  /** Whether this row's service method resolves to eService. Derived from the underlying method id/value, not the display label. */
  isEService: boolean;
}

export enum ServiceMethodEnum {
  ESERVICE = 1,
  MAIL = 4,
}

/** The canonical display label for the eService method. Shared so producers and consumers stay in sync. */
export const ESERVICE_LABEL = 'eService';

/**
 * Determines whether a representation's service method resolves to eService.
 * Prefers the underlying `serviceMethodId` (authoritative); only falls back to the
 * normalized API string when no id is present, mirroring {@link displayServiceMethodForRepresentation}.
 * @param attorney - The representation to evaluate.
 * @returns True when the representation is served via eService.
 */
export function isEServiceRepresentation(attorney: Representation | null): boolean {
  if (!attorney) {
    return false;
  }
  if (attorney.serviceMethodId != null) {
    return attorney.serviceMethodId === ServiceMethodEnum.ESERVICE;
  }
  return attorney.serviceMethod?.trim().toLowerCase() === ESERVICE_LABEL.toLowerCase();
}

/**
 * Displays the service method for a representation.
 * @param attorney - The representation to display the service method for.
 * @returns The service method to display.
 */
export function displayServiceMethodForRepresentation(attorney: Representation | null): string {
  // If no attorney is present, we want to just show US mail as the service method
  if (!attorney) {
    return 'U.S. Mail';
  }
  if (attorney.serviceMethodId === ServiceMethodEnum.ESERVICE) {
    return ESERVICE_LABEL;
  }
  if (attorney.serviceMethodId === ServiceMethodEnum.MAIL) {
    return 'U.S. Mail';
  }
  const fromApi = attorney.serviceMethod?.trim();
  if (fromApi) {
    return fromApi;
  }
  return '-';
}

/**
 * "Dismissed" / "Withdrawn" for non-active participants; null otherwise.
 * @param cp - The case participant to evaluate.
 * @returns The status label to display, or null for active participants.
 */
function partyStatusLabel(cp: CaseParticipantWithOptionalStatus): string | null {
  return cp.partyStatus === PARTY_STATUS.DISMISSED ||
    cp.partyStatus === PARTY_STATUS.WITHDRAWN
    ? cp.partyStatus
    : null;
}

/**
 * Builds a single list row from a CaseParticipant and optional Representation.
 */
export function buildCaseParticipantListRow(
  cp: CaseParticipant,
  attorney: Representation | null,
  partyName: string,
): CaseParticipantListRow {
  const baseId = cp.caseParticipantUniqueId ?? `${cp.party?.partyId}`;
  const serviceMethod = displayServiceMethodForRepresentation(attorney);
  const isEService = isEServiceRepresentation(attorney);
  return {
    id: attorney ? `${baseId}_${attorney.attorneyId ?? ''}` : baseId,
    partyId: cp.party?.partyId ?? null,
    caseParticipant: cp,
    representation: attorney,
    partyName,
    partyTypeLabel: cp.party?.partyType ?? '',
    partyStatusLabel: partyStatusLabel(cp),
    attorneyName: attorney?.attorneyName ?? NO_ANSWER_ON_FILE,
    attorneyTypeLabel: attorney?.attorneyType ?? '',
    firmName: attorney?.attorneyOrganizationName ?? '',
    serviceMethod,
    isEService,
  };
}

/**
 * Flattens {@link CaseParticipant}[] into {@link CaseParticipantListRow}[] for table display:
 * one row per party–attorney pair (multiple attorneys on one participant → multiple rows, same party name).
 * If `attorneys` is empty, a single row is still emitted with representation fields empty.
 */
export function flattenCaseParticipantsToListRows(
  participants: CaseParticipant[],
): CaseParticipantListRow[] {
  const rows: CaseParticipantListRow[] = [];
  participants.forEach(cp => {
    const party = cp.party;
    let partyName: string;
    if (party?.firstName) {
      partyName = `${party.firstName} ${party.lastName ?? ''}`.trim();
    } else if (party?.lastName) {
      partyName = party.lastName;
    } else {
      partyName = '';
    }
    const attorneys = cp.attorneys ?? [];

    if (attorneys.length === 0) {
      rows.push(buildCaseParticipantListRow(cp, null, partyName));
    } else {
      attorneys.forEach(attorney =>
        rows.push(buildCaseParticipantListRow(cp, attorney, partyName)),
      );
    }
  });
  return rows;
}
